import React, { useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { toast } from "sonner";
import { X, Upload, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

/**
 * Modal para importar una oferta desde un JSON exportado de otro entorno.
 * Uso típico: preview → producción. El usuario copia el JSON de la oferta
 * en preview (botón "Copiar payload" del OfferDetail), lo pega aquí, y le da
 * a "Importar". Se crea 1 registro nuevo con UUID fresco — cero riesgo para
 * las ofertas existentes.
 */
export default function ImportOfferJsonModal({ open, onClose, onImported }) {
  const [json, setJson] = useState("");
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const parsed = (() => {
    if (!json.trim()) return null;
    try { return JSON.parse(json); } catch { return "PARSE_ERROR"; }
  })();
  const isValid = parsed && parsed !== "PARSE_ERROR"
    && typeof parsed.title === "string"
    && Array.isArray(parsed.cost_sections);

  const doImport = async () => {
    if (!isValid) return;
    if (!window.confirm("⚠ Vas a IMPORTAR una oferta TIC desde JSON externo. Se creará un registro nuevo. ¿Continuar?")) return;
    setImporting(true);
    setError(null);
    try {
      const { data } = await axios.post(`${API}/offers/import-payload`, parsed);
      toast.success("Oferta importada", {
        description: `id ${data.id} — ${data.cost_sections?.length} secciones`,
      });
      onImported?.(data);
      onClose?.();
      // Redirige al detalle para verificar
      setTimeout(() => navigate(`/offers/${data.id}`), 300);
    } catch (e) {
      const status = e.response?.status;
      let msg;
      if (status === 401 || status === 403) {
        msg = "No tienes permiso para importar ofertas (se requiere acceso de escritura al módulo Ofertas). Pide a un administrador que te dé el permiso o que la importe por ti.";
      } else if (status === 404 || status === 405) {
        msg = "El importador aún no está disponible en este entorno. Si estás en producción, puede faltar publicar la última versión: vuelve a intentarlo tras el despliegue.";
      } else {
        const d = e.response?.data?.detail || e.message;
        msg = typeof d === "string" ? d : JSON.stringify(d);
      }
      setError(msg);
    } finally {
      setImporting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="import-offer-json-modal">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold font-['Outfit'] flex items-center gap-2">
              <Upload size={18} className="text-[#007934]"/>
              Importar oferta desde JSON
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Pega el JSON exportado desde otro entorno (ej. Preview → Producción). Crea 1 registro nuevo, sin tocar el resto.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700"><X size={18}/></button>
        </div>

        <div className="p-4 flex-1 overflow-y-auto">
          <label className="text-xs font-semibold text-gray-700 mb-1 block">
            Payload JSON (obligatorio)
          </label>
          <textarea
            value={json}
            onChange={e => { setJson(e.target.value); setError(null); }}
            placeholder='{"title": "...", "cost_sections": [...] }'
            rows={16}
            className="w-full font-mono text-[11px] border border-gray-200 rounded-lg p-3 focus:border-[#007934] focus:outline-none"
            data-testid="import-offer-json-textarea"
          />

          <div className="mt-3 text-xs">
            {json.trim() === "" && (
              <div className="text-gray-400">
                Ejemplo mínimo: <code>{"{ \"title\": \"…\", \"cost_sections\": [...] }"}</code>
              </div>
            )}
            {json.trim() && parsed === "PARSE_ERROR" && (
              <div className="flex items-center gap-1.5 text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                <AlertCircle size={13}/>
                <span>JSON no válido — comprueba comas, comillas y llaves.</span>
              </div>
            )}
            {isValid && (
              <div className="flex items-center gap-1.5 text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
                <CheckCircle size={13}/>
                <span>
                  JSON válido · <b>{parsed.title}</b> · {parsed.cost_sections?.length} secciones
                  {parsed.municipality && ` · ${parsed.municipality}`}
                </span>
              </div>
            )}
            {error && (
              <div className="mt-2 flex items-start gap-1.5 text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">
                <AlertCircle size={13} className="flex-shrink-0 mt-0.5"/>
                <span className="break-all">{error}</span>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-gray-100 p-3 flex justify-end gap-2">
          <button onClick={onClose} className="border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">
            Cancelar
          </button>
          <button
            onClick={doImport}
            disabled={!isValid || importing}
            className="bg-[#007934] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#006528] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
            data-testid="import-offer-json-submit"
          >
            {importing ? <Loader2 size={13} className="animate-spin"/> : <Upload size={13}/>}
            {importing ? "Importando…" : "Importar"}
          </button>
        </div>
      </div>
    </div>
  );
}
